export const visitorScopeGroupCodes = [
  'visitorAdmin',
  'visitorIntegration',
  'visitorConfigurations',
  'visitorAlertSettings',
  'visitorLocation',
  'visitorUser',
  'visitorInvitation',
  'visitorSigninConf',
  'visitorSignoutConf',
  'visitorKiosk',
  'visitorAgreements',
  'visitorRedflagConf',
  'visitorFields',
  'visitorEmailTemplates',
  'visitorDash',
  'visitorVisitors',
  'visitorInvite',
  'visitorAllVisitors',
  'visitorVisitorLog',
  'visitorSignIn',
  'visitorSignOut',
  'visitorDeny',
  'visitorRedFlag',
  'visitorGuestPass',
  'visitorVisitorsBulk',
  'visitorRpt',
  'visitorRptVisitor',
  'visitorRptTrend',
  'visitorRptRedFlag',
  'visitorRptHost',
  'visitorRptActivity',
  'visitorRptGuestPass',
] as const;

export type VisitorScopeGroupCode = (typeof visitorScopeGroupCodes)[number];

export const visitorScopeTemplates = visitorScopeGroupCodes.flatMap(
  (scopeGroup) => [`o{orgId}-${scopeGroup}:r`, `o{orgId}-${scopeGroup}:w`],
);

export const getVisitorScopesForOrg = (orgId?: number | string | null) =>
  visitorScopeGroupCodes.flatMap((scopeGroup) => [
    `o${orgId}-${scopeGroup}:r`,
    `o${orgId}-${scopeGroup}:w`,
  ]);

// Scopes accepted by helium's GET /orgs/{orgId}/locations route. Only fetch the
// locations list when the user has one of these, otherwise the request 403s
// with "method that get a locations". Used to gate the VisitorDataLoader
// welcome-page redirect / loading block so limited-scope roles (e.g.
// invitation-only) aren't wrongly redirected to the welcome page. Kept in sync
// with the scope array on helium's visitorLocation GET /locations route.
export const locationReadScopes = [
  'o{orgId}-visitorLocation:w',
  'o{orgId}-visitorLocation:r',
  'o{orgId}-visitorAdmin:w',
  'o{orgId}-visitorAdmin:r',
  'o{orgId}-visitorIntegration:r',
  'o{orgId}-visitorIntegration:w',
  'o{orgId}-visitorConfigurations:w',
  'o{orgId}-visitorConfigurations:r',
  'o{orgId}-visitorDash:w',
  'o{orgId}-visitorDash:r',
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorInvite:r',
  'o{orgId}-visitorInvite:w',
  'o{orgId}-visitorAllVisitors:r',
  'o{orgId}-visitorAllVisitors:w',
  'o{orgId}-visitorVisitorLog:r',
  'o{orgId}-visitorVisitorLog:w',
  'o{orgId}-visitorSignIn:r',
  'o{orgId}-visitorSignIn:w',
  'o{orgId}-visitorSignOut:r',
  'o{orgId}-visitorSignOut:w',
  'o{orgId}-visitorDeny:r',
  'o{orgId}-visitorDeny:w',
  'o{orgId}-visitorRedFlag:r',
  'o{orgId}-visitorRedFlag:w',
  'o{orgId}-visitorGuestPass:r',
  'o{orgId}-visitorGuestPass:w',
  'o{orgId}-visitorVisitorsBulk:r',
  'o{orgId}-visitorVisitorsBulk:w',
  'o{orgId}-visitorRpt:w',
  'o{orgId}-visitorRpt:r',
  'o{orgId}-visitorRptVisitor:r',
  'o{orgId}-visitorRptVisitor:w',
  'o{orgId}-visitorRptTrend:r',
  'o{orgId}-visitorRptTrend:w',
  'o{orgId}-visitorRptRedFlag:r',
  'o{orgId}-visitorRptRedFlag:w',
  'o{orgId}-visitorRptHost:r',
  'o{orgId}-visitorRptHost:w',
  'o{orgId}-visitorRptActivity:r',
  'o{orgId}-visitorRptActivity:w',
  'o{orgId}-visitorRptGuestPass:r',
  'o{orgId}-visitorRptGuestPass:w',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes that grant read access to all visitors (not just visits the current
// user hosts). Used to gate the "all visitors" filter applied across reports so
// limited-scope roles only see the visits they host. Mirrors the inline scope
// arrays used by the report components (e.g. GuestPassReportTable, Dashboard).
// NOTE: visitorAllVisitors:r is a child of the visitorVisitors:r parent scope
// (see helium seed-visitor-scope migration). A role granted the parent
// visitorVisitors:r implicitly includes visitorAllVisitors:r but the child code
// is not present in the token, so the parent must be checked here too — without
// it, standard (non-granular) roles would be wrongly host-filtered.
export const visitorAllVisitorsReadScopes = [
  'o{orgId}-visitorAllVisitors:r',
  'o{orgId}-visitorVisitors:r',
  'o:r',
  's-o:r',
];

// The parent visitorVisitors scope, in either read OR write mode. A role
// granted the parent scope implicitly covers all its child scopes (including
// visitorAllVisitors), so it must never be host-filtered — even when only the
// write variant is present (e.g. a role that can create/edit visits but whose
// token doesn't carry the visitorVisitors:r / visitorAllVisitors:r read code).
export const visitorVisitorsParentScopes = [
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorVisitors:w',
  'o:r',
  'o:w',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET /orgs/{orgId}/visitor/termsAgreements route
// (listAllDocument). Kept in sync with the scope array on helium's
// visitorTermsAgreements GET /visitor/termsAgreements route so limited-scope
// roles don't 403 fetching the org-wide agreements list.
export const agreementsReadScopes = [
  'o{orgId}-visitorAgreements:w',
  'o{orgId}-visitorAgreements:r',
  'o{orgId}-visitorAdmin:w',
  'o{orgId}-visitorAdmin:r',
  'o{orgId}-visitorIntegration:r',
  'o{orgId}-visitorIntegration:w',
  'o{orgId}-visitorConfigurations:w',
  'o{orgId}-visitorConfigurations:r',
  'o{orgId}-visitorDash:w',
  'o{orgId}-visitorDash:r',
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorInvite:r',
  'o{orgId}-visitorInvite:w',
  'o{orgId}-visitorAllVisitors:r',
  'o{orgId}-visitorAllVisitors:w',
  'o{orgId}-visitorVisitorLog:r',
  'o{orgId}-visitorVisitorLog:w',
  'o{orgId}-visitorSignIn:r',
  'o{orgId}-visitorSignIn:w',
  'o{orgId}-visitorSignOut:r',
  'o{orgId}-visitorSignOut:w',
  'o{orgId}-visitorDeny:r',
  'o{orgId}-visitorDeny:w',
  'o{orgId}-visitorRedFlag:r',
  'o{orgId}-visitorRedFlag:w',
  'o{orgId}-visitorGuestPass:r',
  'o{orgId}-visitorGuestPass:w',
  'o{orgId}-visitorVisitorsBulk:r',
  'o{orgId}-visitorVisitorsBulk:w',
  'o{orgId}-visitorRpt:w',
  'o{orgId}-visitorRpt:r',
  'o{orgId}-visitorRptVisitor:r',
  'o{orgId}-visitorRptVisitor:w',
  'o{orgId}-visitorRptTrend:r',
  'o{orgId}-visitorRptTrend:w',
  'o{orgId}-visitorRptRedFlag:r',
  'o{orgId}-visitorRptRedFlag:w',
  'o{orgId}-visitorRptHost:r',
  'o{orgId}-visitorRptHost:w',
  'o{orgId}-visitorRptActivity:r',
  'o{orgId}-visitorRptActivity:w',
  'o{orgId}-visitorRptGuestPass:r',
  'o{orgId}-visitorRptGuestPass:w',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET /orgs/{orgId}/redflag route (listAllRedFlag).
// Kept in sync with the scope array on helium's visitorRedFlag GET /redflag
// route so limited-scope roles don't 403 fetching red flags.
export const redFlagReadScopes = [
  'o{orgId}-visitorRedFlag:w',
  'o{orgId}-visitorRedFlag:r',
  'o{orgId}-visitorRptRedFlag:w',
  'o{orgId}-visitorRptRedFlag:r',
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorRptVisitor:r',
  'o{orgId}-visitorRptHost:r',
  'o{orgId}-visitorRpt:r',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET
// /orgs/{orgId}/visitor/{visitId}/visitorAgreement route (getVisitorAgreement).
// Kept in sync with the scope array on helium's visitor GET
// /visitor/{visitId}/visitorAgreement route so limited-scope roles (e.g.
// dashboard-only) don't 403 fetching the visitor's signed agreements.
export const visitorAgreementReadScopes = [
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorInvite:r',
  'o{orgId}-visitorInvite:w',
  'o{orgId}-visitorAllVisitors:r',
  'o{orgId}-visitorAllVisitors:w',
  'o{orgId}-visitorVisitorLog:r',
  'o{orgId}-visitorVisitorLog:w',
  'o{orgId}-visitorSignIn:r',
  'o{orgId}-visitorSignIn:w',
  'o{orgId}-visitorSignOut:r',
  'o{orgId}-visitorSignOut:w',
  'o{orgId}-visitorDeny:r',
  'o{orgId}-visitorDeny:w',
  'o{orgId}-visitorRedFlag:r',
  'o{orgId}-visitorRedFlag:w',
  'o{orgId}-visitorGuestPass:r',
  'o{orgId}-visitorGuestPass:w',
  'o{orgId}-visitorVisitorsBulk:r',
  'o{orgId}-visitorVisitorsBulk:w',
  'o{orgId}-visitorRpt:r',
  'o{orgId}-visitorRptVisitor:r',
  'o{orgId}-visitorRptRedFlag:r',
  'o{orgId}-visitorRptHost:r',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET /orgs/{orgId}/kiosk route (listAllKiosk).
// Kept in sync with the scope array on helium's visitorKiosk GET /kiosk route
// so limited-scope roles (e.g. dashboard-only) don't 403 fetching kiosks.
export const kioskReadScopes = [
  'o{orgId}-visitorKiosk:w',
  'o{orgId}-visitorKiosk:r',
  'o{orgId}-visitorConfigurations:w',
  'o{orgId}-visitorConfigurations:r',
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorInvite:r',
  'o{orgId}-visitorInvite:w',
  'o{orgId}-visitorAllVisitors:r',
  'o{orgId}-visitorAllVisitors:w',
  'o{orgId}-visitorVisitorLog:r',
  'o{orgId}-visitorVisitorLog:w',
  'o{orgId}-visitorSignIn:r',
  'o{orgId}-visitorSignIn:w',
  'o{orgId}-visitorSignOut:r',
  'o{orgId}-visitorSignOut:w',
  'o{orgId}-visitorDeny:r',
  'o{orgId}-visitorDeny:w',
  'o{orgId}-visitorRedFlag:r',
  'o{orgId}-visitorRedFlag:w',
  'o{orgId}-visitorGuestPass:r',
  'o{orgId}-visitorGuestPass:w',
  'o{orgId}-visitorVisitorsBulk:r',
  'o{orgId}-visitorVisitorsBulk:w',
  'o{orgId}-visitorRptVisitor:r',
  'o{orgId}-visitorRptRedFlag:r',
  'o{orgId}-visitorRptHost:r',
  'o{orgId}-visitorRpt:r',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET /orgs/{orgId}/visitor/{visitId}/visitorData
// route (getVisitorData). Kept in sync with the scope array on helium's
// visitorVisitor GET /visitor/{visitId}/visitorData route so limited-scope
// roles (e.g. dashboard-only) don't 403 fetching the visit's custom field data.
export const visitorDataReadScopes = [
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorInvite:r',
  'o{orgId}-visitorInvite:w',
  'o{orgId}-visitorAllVisitors:r',
  'o{orgId}-visitorAllVisitors:w',
  'o{orgId}-visitorVisitorLog:r',
  'o{orgId}-visitorVisitorLog:w',
  'o{orgId}-visitorSignIn:r',
  'o{orgId}-visitorSignIn:w',
  'o{orgId}-visitorSignOut:r',
  'o{orgId}-visitorSignOut:w',
  'o{orgId}-visitorDeny:r',
  'o{orgId}-visitorDeny:w',
  'o{orgId}-visitorRedFlag:r',
  'o{orgId}-visitorRedFlag:w',
  'o{orgId}-visitorGuestPass:r',
  'o{orgId}-visitorGuestPass:w',
  'o{orgId}-visitorVisitorsBulk:r',
  'o{orgId}-visitorVisitorsBulk:w',
  'o{orgId}-visitorRpt:r',
  'o{orgId}-visitorRptVisitor:r',
  'o{orgId}-visitorRptTrend:r',
  'o{orgId}-visitorRptRedFlag:r',
  'o{orgId}-visitorRptHost:r',
  'o{orgId}-visitorRptActivity:r',
  'o{orgId}-visitorRptGuestPass:r',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET
// /orgs/{orgId}/visitorWorkflow/{workflowId}/fieldCustom route
// (getVisitorWorkflowFieldsCustom). Kept in sync with the scope array on
// helium's visitorWorkflow GET /visitorWorkflow/{workflowId}/fieldCustom route
// so limited-scope roles don't 403 fetching the workflow custom field config.
export const visitorWorkflowFieldsCustomReadScopes = [
  'o{orgId}-visitorAllVisitors:w',
  'o{orgId}-visitorAllVisitors:r',
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorConfigurations:w',
  'o{orgId}-visitorConfigurations:r',
  'o{orgId}-visitorFields:w',
  'o{orgId}-visitorFields:r',
  'o{orgId}-visitorSigninConf:w',
  'o{orgId}-visitorSigninConf:r',
  'o{orgId}-visitorInvite:w',
  'o{orgId}-visitorInvite:r',
  'o{orgId}-visitorRpt:r',
  'o{orgId}-visitorRptTrend:r',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET /orgs/{orgId}/visitor/{visitId}/documentCheck
// route (getVisitDocumentCheck). Kept in sync with the scope array on helium's
// visitor GET /visitor/{visitId}/documentCheck route so limited-scope roles
// (e.g. dashboard-only) don't 403 fetching the visitor document check.
export const visitorDocumentCheckReadScopes = [
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorInvite:r',
  'o{orgId}-visitorInvite:w',
  'o{orgId}-visitorAllVisitors:r',
  'o{orgId}-visitorAllVisitors:w',
  'o{orgId}-visitorVisitorLog:r',
  'o{orgId}-visitorVisitorLog:w',
  'o{orgId}-visitorSignIn:r',
  'o{orgId}-visitorSignIn:w',
  'o{orgId}-visitorSignOut:r',
  'o{orgId}-visitorSignOut:w',
  'o{orgId}-visitorDeny:r',
  'o{orgId}-visitorDeny:w',
  'o{orgId}-visitorRedFlag:r',
  'o{orgId}-visitorRedFlag:w',
  'o{orgId}-visitorGuestPass:r',
  'o{orgId}-visitorGuestPass:w',
  'o{orgId}-visitorVisitorsBulk:r',
  'o{orgId}-visitorVisitorsBulk:w',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET /orgs/{orgId}/visitor/{visitId}/qrScanLog
// route (getQrScanLog). Kept in sync with the scope array on helium's visitor
// GET /visitor/{visitId}/qrScanLog route so limited-scope roles (e.g.
// dashboard-only) don't 403 fetching the QR code scan log.
export const qrScanLogReadScopes = [
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorInvite:r',
  'o{orgId}-visitorInvite:w',
  'o{orgId}-visitorAllVisitors:r',
  'o{orgId}-visitorAllVisitors:w',
  'o{orgId}-visitorVisitorLog:r',
  'o{orgId}-visitorVisitorLog:w',
  'o{orgId}-visitorSignIn:r',
  'o{orgId}-visitorSignIn:w',
  'o{orgId}-visitorSignOut:r',
  'o{orgId}-visitorSignOut:w',
  'o{orgId}-visitorDeny:r',
  'o{orgId}-visitorDeny:w',
  'o{orgId}-visitorRedFlag:r',
  'o{orgId}-visitorRedFlag:w',
  'o{orgId}-visitorGuestPass:r',
  'o{orgId}-visitorGuestPass:w',
  'o{orgId}-visitorVisitorsBulk:r',
  'o{orgId}-visitorVisitorsBulk:w',
  'o{orgId}-visitorRpt:r',
  'o{orgId}-visitorRptVisitor:r',
  'o{orgId}-visitorRptTrend:r',
  'o{orgId}-visitorRptRedFlag:r',
  'o{orgId}-visitorRptHost:r',
  'o{orgId}-visitorRptActivity:r',
  'o{orgId}-visitorRptGuestPass:r',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET /orgs/{orgId}/integrationMsiAlta route
// (getIntegrationMsiAlta). Kept in sync with the scope array on helium's
// visitorMSIAltaIntegration GET /integrationMsiAlta route so limited-scope
// roles don't 403 fetching the MSI Alta integration status.
export const msiAltaIntegrationReadScopes = [
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorAdmin:w',
  'o{orgId}-visitorAdmin:r',
  'o{orgId}-visitorConfigurations:w',
  'o{orgId}-visitorConfigurations:r',
  'o{orgId}-visitorAlertSettings:r',
  'o{orgId}-visitorAlertSettings:w',
  'o{orgId}-visitorLocation:r',
  'o{orgId}-visitorLocation:w',
  'o{orgId}-visitorUser:r',
  'o{orgId}-visitorUser:w',
  'o{orgId}-visitorInvitation:r',
  'o{orgId}-visitorInvitation:w',
  'o{orgId}-visitorSigninConf:r',
  'o{orgId}-visitorSigninConf:w',
  'o{orgId}-visitorSignoutConf:r',
  'o{orgId}-visitorSignoutConf:w',
  'o{orgId}-visitorKiosk:r',
  'o{orgId}-visitorKiosk:w',
  'o{orgId}-visitorAgreements:r',
  'o{orgId}-visitorAgreements:w',
  'o{orgId}-visitorRedflagConf:r',
  'o{orgId}-visitorRedflagConf:w',
  'o{orgId}-visitorFields:r',
  'o{orgId}-visitorFields:w',
  'o{orgId}-visitorEmailTemplates:r',
  'o{orgId}-visitorEmailTemplates:w',
  'o{orgId}-visitorDash:w',
  'o{orgId}-visitorDash:r',
  'o{orgId}-visitorRpt:w',
  'o{orgId}-visitorRpt:r',
  'o{orgId}-visitorIntegration:w',
  'o{orgId}-visitorIntegration:r',
  'o{orgId}-visitorGuestPass:w',
  'o{orgId}-visitorGuestPass:r',
  'o{orgId}-visitorRptGuestPass:w',
  'o{orgId}-visitorRptGuestPass:r',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET /orgs/{orgId}/integrationMsiAltaVideo route
// (getIntegrationMsiAltaVideo). Kept in sync with the scope array on helium's
// visitorMSIAltaVideoIntegration GET /integrationMsiAltaVideo route so
// limited-scope roles don't 403 fetching the Alta Video integration status.
// Currently identical to msiAltaIntegrationReadScopes, but kept as its own
// constant so each route can drift independently.
export const msiAltaVideoIntegrationReadScopes = [
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorAdmin:w',
  'o{orgId}-visitorAdmin:r',
  'o{orgId}-visitorConfigurations:w',
  'o{orgId}-visitorConfigurations:r',
  'o{orgId}-visitorAlertSettings:r',
  'o{orgId}-visitorAlertSettings:w',
  'o{orgId}-visitorLocation:r',
  'o{orgId}-visitorLocation:w',
  'o{orgId}-visitorUser:r',
  'o{orgId}-visitorUser:w',
  'o{orgId}-visitorInvitation:r',
  'o{orgId}-visitorInvitation:w',
  'o{orgId}-visitorSigninConf:r',
  'o{orgId}-visitorSigninConf:w',
  'o{orgId}-visitorSignoutConf:r',
  'o{orgId}-visitorSignoutConf:w',
  'o{orgId}-visitorKiosk:r',
  'o{orgId}-visitorKiosk:w',
  'o{orgId}-visitorAgreements:r',
  'o{orgId}-visitorAgreements:w',
  'o{orgId}-visitorRedflagConf:r',
  'o{orgId}-visitorRedflagConf:w',
  'o{orgId}-visitorFields:r',
  'o{orgId}-visitorFields:w',
  'o{orgId}-visitorEmailTemplates:r',
  'o{orgId}-visitorEmailTemplates:w',
  'o{orgId}-visitorDash:w',
  'o{orgId}-visitorDash:r',
  'o{orgId}-visitorRpt:w',
  'o{orgId}-visitorRpt:r',
  'o{orgId}-visitorIntegration:w',
  'o{orgId}-visitorIntegration:r',
  'o{orgId}-visitorGuestPass:w',
  'o{orgId}-visitorGuestPass:r',
  'o{orgId}-visitorRptGuestPass:w',
  'o{orgId}-visitorRptGuestPass:r',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET /orgs/{orgId}/users/{userId}/locations route
// (getUserLocations). Kept in sync with the scope array on helium's visitorUser
// GET /users/{userId}/locations route so we only fetch the user's locations
// when the request would be authorized.
export const userLocationsReadScopes = [
  'o{orgId}-visitorUser:w',
  'o{orgId}-visitorUser:r',
  'o{orgId}-visitorConfigurations:w',
  'o{orgId}-visitorConfigurations:r',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET /orgs/{orgId}/visitorWorkflow and GET
// /orgs/{orgId}/visitorWorkflowTypes routes (listAllVisitorWorkflow /
// listAllVisitorWorkflowTypes — both share the same scope array). Kept in sync
// with helium so limited-scope roles don't 403 fetching the workflow list.
export const visitorWorkflowReadScopes = [
  'o{orgId}-visitorSigninConf:w',
  'o{orgId}-visitorSigninConf:r',
  'o{orgId}-visitorSignoutConf:w',
  'o{orgId}-visitorSignoutConf:r',
  'o{orgId}-visitorInvite:w',
  'o{orgId}-visitorInvite:r',
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorConfigurations:w',
  'o{orgId}-visitorConfigurations:r',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET /orgs/{orgId}/visitorWorkflowConfig/{workflowId}
// route (getVisitorWorkflowConfig). Kept in sync with the scope array on
// helium's visitorWorkflow GET /visitorWorkflowConfig/{workflowId} route so
// limited-scope roles don't 403 fetching the sign-in workflow config.
export const visitorWorkflowConfigReadScopes = [
  'o{orgId}-visitorSigninConf:w',
  'o{orgId}-visitorSigninConf:r',
  'o{orgId}-visitorSignoutConf:w',
  'o{orgId}-visitorSignoutConf:r',
  'o{orgId}-visitorAdmin:w',
  'o{orgId}-visitorAdmin:r',
  'o{orgId}-visitorIntegration:r',
  'o{orgId}-visitorIntegration:w',
  'o{orgId}-visitorConfigurations:w',
  'o{orgId}-visitorConfigurations:r',
  'o{orgId}-visitorDash:w',
  'o{orgId}-visitorDash:r',
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorInvite:r',
  'o{orgId}-visitorInvite:w',
  'o{orgId}-visitorAllVisitors:r',
  'o{orgId}-visitorAllVisitors:w',
  'o{orgId}-visitorVisitorLog:r',
  'o{orgId}-visitorVisitorLog:w',
  'o{orgId}-visitorSignIn:r',
  'o{orgId}-visitorSignIn:w',
  'o{orgId}-visitorSignOut:r',
  'o{orgId}-visitorSignOut:w',
  'o{orgId}-visitorDeny:r',
  'o{orgId}-visitorDeny:w',
  'o{orgId}-visitorRedFlag:r',
  'o{orgId}-visitorRedFlag:w',
  'o{orgId}-visitorGuestPass:r',
  'o{orgId}-visitorGuestPass:w',
  'o{orgId}-visitorVisitorsBulk:r',
  'o{orgId}-visitorVisitorsBulk:w',
  'o{orgId}-visitorRpt:w',
  'o{orgId}-visitorRpt:r',
  'o{orgId}-visitorRptVisitor:r',
  'o{orgId}-visitorRptVisitor:w',
  'o{orgId}-visitorRptTrend:r',
  'o{orgId}-visitorRptTrend:w',
  'o{orgId}-visitorRptRedFlag:r',
  'o{orgId}-visitorRptRedFlag:w',
  'o{orgId}-visitorRptHost:r',
  'o{orgId}-visitorRptHost:w',
  'o{orgId}-visitorRptActivity:r',
  'o{orgId}-visitorRptActivity:w',
  'o{orgId}-visitorRptGuestPass:r',
  'o{orgId}-visitorRptGuestPass:w',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];

// Scopes accepted by helium's GET
// /orgs/{orgId}/visitorWorkflow/{workflowId}/fieldConfig route
// (getVisitorWorkflowFields). Kept in sync with the scope array on helium's
// visitorWorkflow GET /visitorWorkflow/{workflowId}/fieldConfig route so
// limited-scope roles don't 403 fetching the workflow field config.
export const visitorWorkflowFieldsReadScopes = [
  'o{orgId}-visitorSigninConf:w',
  'o{orgId}-visitorSigninConf:r',
  'o{orgId}-visitorInvite:w',
  'o{orgId}-visitorInvite:r',
  'o{orgId}-visitorAdmin:w',
  'o{orgId}-visitorAdmin:r',
  'o{orgId}-visitorIntegration:r',
  'o{orgId}-visitorIntegration:w',
  'o{orgId}-visitorConfigurations:w',
  'o{orgId}-visitorConfigurations:r',
  'o{orgId}-visitorDash:w',
  'o{orgId}-visitorDash:r',
  'o{orgId}-visitorVisitors:w',
  'o{orgId}-visitorVisitors:r',
  'o{orgId}-visitorRpt:w',
  'o{orgId}-visitorRpt:r',
  'o{orgId}-visitorRptVisitor:r',
  'o{orgId}-visitorRptVisitor:w',
  'o{orgId}-visitorRptTrend:r',
  'o{orgId}-visitorRptTrend:w',
  'o{orgId}-visitorRptRedFlag:r',
  'o{orgId}-visitorRptRedFlag:w',
  'o{orgId}-visitorRptHost:r',
  'o{orgId}-visitorRptHost:w',
  'o{orgId}-visitorRptActivity:r',
  'o{orgId}-visitorRptActivity:w',
  'o{orgId}-visitorRptGuestPass:r',
  'o{orgId}-visitorRptGuestPass:w',
  'o:w',
  'o:r',
  's-o:r',
  's-o:w',
];
